import { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  HelpOutline as HelpOutlineIcon,
  MedicalServices as MedicalServicesIcon,
  Person as PersonIcon,
  Public as PublicIcon,
  Restaurant as RestaurantIcon,
  Science as ScienceIcon,
  Sensors as SensorsIcon,
} from '@mui/icons-material';
import patient360Data from '../../mocks/patient360Data.json';
import {
  loadPublishedProtocols,
  updatePublishedProtocol,
  updatePublishedProtocolShare,
  type PublishedProtocol,
} from '../../utils/publishedProtocolsStore';
import {
  addClinicianGapQuestionComment,
  deleteGapQuestion,
  editGapQuestion,
  loadGapAnalyses,
  updateGapQuestionResolution,
  updateGapQuestionShare,
  type GapDomain,
  type PatientGapAnalysis,
} from '../../utils/patientGapAnalysisStore';
import {
  Patient360CoreTabs,
  Patient360LifestyleTabs,
  Patient360ManagementTabs,
} from '../../components/clinician';

export const Patient360Page = () => {
  const [selectedPatientId, setSelectedPatientId] = useState(patient360Data.assignedPatients[0].id);
  const [tabValue, setTabValue] = useState(0);
  const [publishedProtocols, setPublishedProtocols] = useState<PublishedProtocol[]>([]);
  const [gapAnalyses, setGapAnalyses] = useState<PatientGapAnalysis[]>([]);
  const [expandedProtocolId, setExpandedProtocolId] = useState<string | null>(null);
  const [editingProtocol, setEditingProtocol] = useState<PublishedProtocol | null>(null);
  const [protocolTitle, setProtocolTitle] = useState('');
  const [protocolDescription, setProtocolDescription] = useState('');
  const [protocolStatus, setProtocolStatus] = useState<'ACTIVE' | 'ARCHIVED'>('ACTIVE');
  const [protocolStartDate, setProtocolStartDate] = useState('');
  const [protocolEndDate, setProtocolEndDate] = useState('');

  useEffect(() => {
    setPublishedProtocols(loadPublishedProtocols());
    setGapAnalyses(loadGapAnalyses());
  }, []);

  const selectedPatient = patient360Data.assignedPatients.find((p) => p.id === selectedPatientId);
  const patientDetails = patient360Data.patientDetails[selectedPatientId as keyof typeof patient360Data.patientDetails];
  const selectedPatientProtocols = publishedProtocols.filter((protocol) => protocol.patientId === selectedPatientId);
  const selectedPatientGapAnalyses = gapAnalyses.filter((analysis) => analysis.patientId === selectedPatientId);
  const latestGapAnalysis = selectedPatientGapAnalyses
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())[0];

  const domainColors: Record<GapDomain, 'error' | 'warning' | 'info' | 'success' | 'default'> = {
    CLINICAL: 'error',
    BEHAVIORAL: 'warning',
    CULTURAL: 'info',
    FAMILY: 'success',
    SDOH: 'default',
  };

  const answeredGapQuestions = selectedPatientGapAnalyses.flatMap((analysis) =>
    analysis.questions
      .filter((question) => question.patientStatus === 'ANSWERED')
      .map((question) => ({ analysisId: analysis.id, question }))
  );

  const demographicGapResponses = answeredGapQuestions.filter(({ question }) => question.domain === 'FAMILY');
  const clinicalGapResponses = answeredGapQuestions.filter(({ question }) => question.domain === 'CLINICAL');
  const nutritionGapResponses = answeredGapQuestions.filter(({ question }) => question.domain === 'BEHAVIORAL');
  const socialGapResponses = answeredGapQuestions.filter(
    ({ question }) => question.domain === 'CULTURAL' || question.domain === 'SDOH'
  );

  const getRiskColor = (riskLevel: string): 'error' | 'warning' | 'success' | 'default' => {
    switch (riskLevel) {
      case 'HIGH':
        return 'error';
      case 'MODERATE':
        return 'warning';
      case 'LOW':
        return 'success';
      default:
        return 'default';
    }
  };

  const getCGMStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'INACTIVE':
        return 'error';
      case 'PENDING':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getLabStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'NORMAL':
        return 'success';
      case 'ABOVE_NORMAL':
      case 'BELOW_NORMAL':
        return 'warning';
      case 'CRITICAL':
        return 'error';
      default:
        return 'default';
    }
  };

  const refreshGapAnalyses = () => setGapAnalyses(loadGapAnalyses());

  const handleShareProtocolChange = (protocolId: string, checked: boolean) => {
    updatePublishedProtocolShare(protocolId, checked);
    setPublishedProtocols(loadPublishedProtocols());
  };

  const handleShareGapQuestionChange = (analysisId: string, questionId: string, checked: boolean) => {
    updateGapQuestionShare(analysisId, questionId, checked);
    refreshGapAnalyses();
  };

  const handleAddClinicianComment = (analysisId: string, questionId: string, comment: string) => {
    addClinicianGapQuestionComment(analysisId, questionId, comment);
    refreshGapAnalyses();
  };

  const handleEditGapQuestion = (
    analysisId: string,
    questionId: string,
    updates: { question: string; rationale: string; domain: GapDomain }
  ) => {
    editGapQuestion(analysisId, questionId, updates);
    refreshGapAnalyses();
  };

  const handleDeleteGapQuestion = (analysisId: string, questionId: string) => {
    deleteGapQuestion(analysisId, questionId);
    refreshGapAnalyses();
  };

  const handleGapResolutionChange = (analysisId: string, questionId: string, status: 'OPEN' | 'RESOLVED') => {
    updateGapQuestionResolution(analysisId, questionId, status);
    refreshGapAnalyses();
  };

  const openEditProtocolDialog = (protocol: PublishedProtocol) => {
    setEditingProtocol(protocol);
    setProtocolTitle(protocol.title);
    setProtocolDescription(protocol.description);
    setProtocolStatus(protocol.status);
    setProtocolStartDate(protocol.startDate);
    setProtocolEndDate(protocol.endDate ?? '');
  };

  const closeEditProtocolDialog = () => setEditingProtocol(null);

  const handleSaveProtocolEdits = () => {
    if (!editingProtocol) {
      return;
    }

    updatePublishedProtocol(editingProtocol.id, {
      title: protocolTitle.trim(),
      description: protocolDescription.trim(),
      status: protocolStatus,
      startDate: protocolStartDate,
      endDate: protocolEndDate || undefined,
    });

    setPublishedProtocols(loadPublishedProtocols());
    closeEditProtocolDialog();
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Patient 360° View
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Comprehensive patient data from EHR, HIE, CGM, and patient-reported information
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={3}>
          <Paper sx={{ height: '80vh', overflow: 'auto' }}>
            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white' }}>
              <Typography variant="h6">My Patients</Typography>
              <Typography variant="caption">{patient360Data.assignedPatients.length} assigned</Typography>
            </Box>
            <List>
              {patient360Data.assignedPatients.map((patient) => (
                <ListItem key={patient.id} disablePadding>
                  <ListItemButton
                    selected={selectedPatientId === patient.id}
                    onClick={() => {
                      setSelectedPatientId(patient.id);
                      setTabValue(0);
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: getRiskColor(patient.riskLevel) }}>
                        <PersonIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={`${patient.firstName} ${patient.lastName}`}
                      secondary={
                        <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                          <Chip
                            label={patient.cgmStatus}
                            size="small"
                            color={getCGMStatusColor(patient.cgmStatus)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={`${patient.dataCompleteness}%`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        </Stack>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        <Grid item xs={12} md={9}>
          {selectedPatient && patientDetails && (
            <Paper>
              <Box sx={{ p: 3, bgcolor: 'grey.50' }}>
                <Grid container alignItems="center" spacing={2}>
                  <Grid item>
                    <Avatar sx={{ width: 64, height: 64, bgcolor: getRiskColor(selectedPatient.riskLevel) }}>
                      <PersonIcon sx={{ fontSize: 40 }} />
                    </Avatar>
                  </Grid>
                  <Grid item xs>
                    <Typography variant="h5">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      DOB: {selectedPatient.dateOfBirth} • {selectedPatient.gender} • Last Visit: {selectedPatient.lastVisit}
                    </Typography>
                    <Stack direction="row" spacing={1} mt={1}>
                      <Chip
                        label={`Risk: ${selectedPatient.riskLevel}`}
                        color={getRiskColor(selectedPatient.riskLevel)}
                        size="small"
                      />
                      <Chip
                        label={`CGM: ${selectedPatient.cgmStatus}`}
                        color={getCGMStatusColor(selectedPatient.cgmStatus)}
                        size="small"
                      />
                      <Chip
                        label={`Data Completeness: ${selectedPatient.dataCompleteness}%`}
                        color={selectedPatient.dataCompleteness > 80 ? 'success' : 'warning'}
                        size="small"
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={tabValue}
                  onChange={(_e, newValue) => setTabValue(newValue)}
                  variant="scrollable"
                  scrollButtons="auto"
                >
                  <Tab icon={<PersonIcon />} label="Demographics" iconPosition="start" />
                  <Tab icon={<MedicalServicesIcon />} label="Clinical Info" iconPosition="start" />
                  <Tab icon={<ScienceIcon />} label="Labs & Tests" iconPosition="start" />
                  <Tab icon={<SensorsIcon />} label="CGM Data" iconPosition="start" />
                  <Tab icon={<RestaurantIcon />} label="Nutrition" iconPosition="start" />
                  <Tab icon={<AssignmentIcon />} label="Care Plan" iconPosition="start" />
                  <Tab icon={<PublicIcon />} label="Social Context" iconPosition="start" />
                  <Tab icon={<AssignmentIcon />} label="Protocols" iconPosition="start" />
                  <Tab icon={<HelpOutlineIcon />} label="Gap Analysis" iconPosition="start" />
                </Tabs>
              </Box>

              <Box sx={{ px: 3 }}>
                <Patient360CoreTabs
                  value={tabValue}
                  patientDetails={patientDetails}
                  demographicGapResponses={demographicGapResponses}
                  clinicalGapResponses={clinicalGapResponses}
                  domainColors={domainColors}
                  onResolutionChange={handleGapResolutionChange}
                  getLabStatusColor={getLabStatusColor}
                />

                <Patient360LifestyleTabs
                  value={tabValue}
                  patientDetails={patientDetails}
                  nutritionGapResponses={nutritionGapResponses}
                  socialGapResponses={socialGapResponses}
                  domainColors={domainColors}
                  onResolutionChange={handleGapResolutionChange}
                />

                <Patient360ManagementTabs
                  value={tabValue}
                  latestGapAnalysis={latestGapAnalysis}
                  selectedPatientProtocols={selectedPatientProtocols}
                  expandedProtocolId={expandedProtocolId}
                  domainColors={domainColors}
                  onToggleExpandedProtocol={(protocolId) =>
                    setExpandedProtocolId((prev) => (prev === protocolId ? null : protocolId))
                  }
                  onShareProtocolChange={handleShareProtocolChange}
                  onEditProtocol={openEditProtocolDialog}
                  onUpdateProtocolInline={(id, updates) => {
                    updatePublishedProtocol(id, updates);
                    setPublishedProtocols(loadPublishedProtocols());
                  }}
                  onShareGapQuestionChange={handleShareGapQuestionChange}
                  onAddClinicianComment={handleAddClinicianComment}
                  onEditGapQuestion={handleEditGapQuestion}
                  onDeleteGapQuestion={handleDeleteGapQuestion}
                />
              </Box>
            </Paper>
          )}
        </Grid>
      </Grid>

      <Dialog open={Boolean(editingProtocol)} onClose={closeEditProtocolDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Protocol</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Protocol Title"
              value={protocolTitle}
              onChange={(event) => setProtocolTitle(event.target.value)}
            />
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Description"
              value={protocolDescription}
              onChange={(event) => setProtocolDescription(event.target.value)}
            />
            <TextField
              select
              fullWidth
              label="Status"
              value={protocolStatus}
              onChange={(event) => setProtocolStatus(event.target.value as 'ACTIVE' | 'ARCHIVED')}
            >
              <MenuItem value="ACTIVE">Active</MenuItem>
              <MenuItem value="ARCHIVED">Inactive</MenuItem>
            </TextField>
            <TextField
              fullWidth
              type="date"
              label="Start Date"
              InputLabelProps={{ shrink: true }}
              value={protocolStartDate}
              onChange={(event) => setProtocolStartDate(event.target.value)}
            />
            <TextField
              fullWidth
              type="date"
              label="End Date (Optional)"
              InputLabelProps={{ shrink: true }}
              value={protocolEndDate}
              onChange={(event) => setProtocolEndDate(event.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditProtocolDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveProtocolEdits}
            disabled={!protocolTitle.trim() || !protocolStartDate.trim()}
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
